import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { Client } from "../types";
import type { MapCamera, MapCanvasHandle, MapClient } from "./MapCanvas";

type Props = {
  clients: Client[];
  center: { lat: number; lng: number };
  zoom: number;
  markerLabelMinZoom: number;
  mapLabel: string;
  satelliteLabel: string;
  accentColor: string;
  overlayActionBackgroundColor: string;
  mapDimStrength: number;
  optimizedTileLoading: boolean;
  visibleClientIds: ReadonlySet<number>;
  highlightedClientId?: number | null;
  onSelect?: (client: Client) => void;
  onActivity?: () => void;
};

function mapClients(clients: Client[]): MapClient[] {
  return clients.map(({ id, name, latitude, longitude }) => ({ id, name, latitude, longitude }));
}

const MapFrameHost = forwardRef<MapCanvasHandle, Props>(function MapFrameHost(props, forwardedRef) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const ready = useRef(false);
  const latest = useRef(props);
  const camera = useRef<MapCamera>({ center: props.center, zoom: props.zoom });
  const requestId = useRef(0);
  const pending = useRef(new Map<number, () => void>());
  latest.current = props;

  const post = (message: Record<string, unknown>) => {
    if (!ready.current) return;
    frame.current?.contentWindow?.postMessage(message, window.location.origin);
  };
  const postData = () => post({ type: "SHOWROOM_MAP_DATA", clients: mapClients(latest.current.clients) });
  const postConfig = () => post({
    type: "SHOWROOM_MAP_CONFIG",
    config: {
      center: latest.current.center,
      zoom: latest.current.zoom,
      markerLabelMinZoom: latest.current.markerLabelMinZoom,
      mapLabel: latest.current.mapLabel,
      satelliteLabel: latest.current.satelliteLabel,
      accentColor: latest.current.accentColor,
      overlayActionBackgroundColor: latest.current.overlayActionBackgroundColor,
      mapDimStrength: latest.current.mapDimStrength,
      optimizedTileLoading: latest.current.optimizedTileLoading
    }
  });
  const postMarkers = () => post({
    type: "SHOWROOM_MAP_MARKERS",
    visibleClientIds: Array.from(latest.current.visibleClientIds),
    highlightedClientId: latest.current.highlightedClientId ?? null
  });

  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === "SHOWROOM_MAP_READY") {
        ready.current = true;
        postConfig();
        postData();
        postMarkers();
      }
      if (event.data?.type === "SHOWROOM_MAP_ACTIVITY") latest.current.onActivity?.();
      if (event.data?.type === "SHOWROOM_MAP_SELECT") {
        const client = latest.current.clients.find((item) => item.id === Number(event.data.clientId));
        if (client) latest.current.onSelect?.(client);
      }
      if (event.data?.type === "SHOWROOM_MAP_CAMERA" && event.data.camera) camera.current = event.data.camera;
      if (event.data?.type === "SHOWROOM_MAP_COMMAND_DONE") {
        const id = Number(event.data.requestId);
        pending.current.get(id)?.();
        pending.current.delete(id);
      }
    };
    window.addEventListener("message", receiveMessage);
    return () => {
      window.removeEventListener("message", receiveMessage);
      pending.current.forEach((resolve) => resolve());
      pending.current.clear();
    };
  }, []);

  useEffect(() => { postData(); }, [props.clients]);
  useEffect(() => { postConfig(); }, [
    props.center.lat, props.center.lng, props.zoom, props.markerLabelMinZoom,
    props.mapLabel, props.satelliteLabel, props.accentColor, props.overlayActionBackgroundColor,
    props.mapDimStrength, props.optimizedTileLoading
  ]);
  useEffect(() => { postMarkers(); }, [props.visibleClientIds, props.highlightedClientId]);

  useImperativeHandle(forwardedRef, () => ({
    focusClient: (client, zoom) => new Promise<void>((resolve) => {
      const id = ++requestId.current;
      const finish = () => resolve();
      pending.current.set(id, finish);
      post({ type: "SHOWROOM_MAP_COMMAND", command: "focusClient", clientId: client.id, zoom, requestId: id });
      window.setTimeout(() => {
        if (!pending.current.has(id)) return;
        pending.current.delete(id);
        finish();
      }, 500);
    }),
    resetHome: (center, zoom) => {
      camera.current = { center, zoom };
      post({ type: "SHOWROOM_MAP_COMMAND", command: "resetHome", center, zoom });
    },
    getCamera: () => camera.current,
    restoreCamera: (nextCamera) => {
      camera.current = nextCamera;
      post({ type: "SHOWROOM_MAP_COMMAND", command: "restoreCamera", camera: nextCamera });
    }
  }), []);

  return <section className="map-iframe-shell" aria-label="Interactive project map">
    <iframe ref={frame} className="map-iframe" src="/map-frame" title="Interactive project map" loading="eager" />
  </section>;
});

export default MapFrameHost;
